import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { 
  Database, 
  Download, 
  Upload, 
  Clock, 
  Play, 
  Settings2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  HardDrive,
  Cloud,
  RefreshCw,
  Trash2,
  FileArchive,
  Calendar,
  Shield,
  Loader2,
  Wifi,
  WifiOff
} from "lucide-react";

type NasHealthStatus = 'unknown' | 'ok' | 'error';

interface BackupRecord {
  id: string;
  backup_type: 'manual' | 'scheduled';
  destination: 'local' | 'cloud' | 'nas' | 'both';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  file_name: string | null;
  file_size_mb: number | null;
  tables_count: number | null;
  rows_count: number | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  created_by: string | null;
}

interface BackupConfig {
  enabled: boolean;
  schedule: string;
  destination: 'local' | 'cloud' | 'nas' | 'both';
  retention_days: number;
  include_tables: string[];
  exclude_tables: string[];
  compress: boolean;
  encrypt: boolean;
}

// Presets de planification
const SCHEDULE_PRESETS = [
  { value: '0 2 * * *', label: 'Quotidien à 2h', description: 'Sauvegarde nocturne' },
  { value: '0 3 * * *', label: 'Quotidien à 3h', description: 'Sauvegarde nocturne' },
  { value: '0 0 * * 0', label: 'Hebdomadaire (Dimanche)', description: 'Chaque dimanche à minuit' },
  { value: '0 0 * * 1', label: 'Hebdomadaire (Lundi)', description: 'Chaque lundi à minuit' },
  { value: '0 0 1 * *', label: 'Mensuel (1er)', description: 'Le 1er de chaque mois' },
  { value: '0 0 15 * *', label: 'Mensuel (15)', description: 'Le 15 de chaque mois' },
  { value: '0 */6 * * *', label: 'Toutes les 6 heures', description: '4 fois par jour' },
  { value: '0 */12 * * *', label: 'Toutes les 12 heures', description: '2 fois par jour' },
];

// Liste des tables principales
const MAIN_TABLES = [
  'profiles', 'categories', 'products', 'orders', 'order_items', 
  'relays', 'vendor_applications', 'vendor_transactions', 'vendor_balances',
  'payments', 'pickup_tokens', 'carts', 'favorites', 'disputes',
  'translations', 'site_settings', 'cms_content', 'notifications'
];

// Liste des buckets Storage
const STORAGE_BUCKETS = [
  'avatars',
  'products', 
  'vendor-documents',
  'site-assets',
  'category-images',
  'relay-images'
];

export function DatabaseBackupManager() {
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [config, setConfig] = useState<BackupConfig>({
    enabled: false,
    schedule: '0 2 * * *',
    destination: 'cloud',
    retention_days: 30,
    include_tables: MAIN_TABLES,
    exclude_tables: [],
    compress: true,
    encrypt: true
  });
  
  const [loading, setLoading] = useState(true);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [storageBackupInProgress, setStorageBackupInProgress] = useState(false);
  const [nasBackupInProgress, setNasBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restorePreview, setRestorePreview] = useState<{ tables: string[], totalRows: number } | null>(null);
  const [backupProgress, setBackupProgress] = useState(0);
  const [configDialog, setConfigDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupRecord | null>(null);
  const [schedulePreset, setSchedulePreset] = useState('');
  const [nasHealthStatus, setNasHealthStatus] = useState<NasHealthStatus>('unknown');
  const [nasHealthMessage, setNasHealthMessage] = useState('Connexion NAS non testée');
  const [nasHealthChecking, setNasHealthChecking] = useState(false);
  const [nasBackupEnabled, setNasBackupEnabled] = useState(false);
  const [nasBackupToggleLoading, setNasBackupToggleLoading] = useState(false);
  const [nasCronInstalled, setNasCronInstalled] = useState<boolean | null>(null);
  const [nasCronLoading, setNasCronLoading] = useState(false);
  const [restoreMode, setRestoreMode] = useState<'dry_run' | 'merge' | 'replace'>('dry_run');

  const getReadableError = (message: string): string => {
    if (!message) return "Une erreur est survenue";
    if (message.includes('Synology WebDAV not configured')) {
      return "Configuration NAS manquante: définissez SYNOLOGY_WEBDAV_URL, SYNOLOGY_WEBDAV_USERNAME et SYNOLOGY_WEBDAV_PASSWORD dans les secrets Supabase.";
    }
    return message;
  };

  // Charger l'historique des sauvegardes
  const fetchBackups = useCallback(async () => {
    try {
      // Note: database_backups n'est pas encore dans les types générés
      const { data, error } = await (supabase as any)
        .from('database_backups')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) {
        // Table doesn't exist yet, that's OK
        if (error.code === '42P01') {
          console.log('Backup table not created yet');
          setBackups([]);
          return;
        }
        throw error;
      }

      setBackups(data as BackupRecord[] || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger la configuration
  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'database_backup_config')
        .single();

      if (data?.value) {
        const savedConfig = typeof data.value === 'string' 
          ? JSON.parse(data.value) 
          : data.value;
        setConfig(prev => ({ ...prev, ...savedConfig }));
      }

      const { data: nasFlagData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'nas_backup_enabled')
        .single();

      if (nasFlagData?.value !== undefined && nasFlagData?.value !== null) {
        setNasBackupEnabled(Boolean(nasFlagData.value));
      }

      const { data: cronStatus } = await (supabase as any)
        .rpc('configure_nas_backup_cron', { p_action: 'status' });

      if (cronStatus && typeof cronStatus.jobs_installed === 'boolean') {
        setNasCronInstalled(cronStatus.jobs_installed);
      }
    } catch (error) {
      // Config not found, use defaults
      console.log('Using default backup config');
    }
  }, []);

  const installSafeNasCron = async () => {
    try {
      setNasCronLoading(true);

      const { data, error } = await (supabase as any)
        .rpc('configure_nas_backup_cron', { p_action: 'install' });

      if (error) throw error;

      const installed = Boolean(data?.jobs_installed);
      setNasCronInstalled(installed);
      setNasBackupEnabled(Boolean(data?.nas_backup_enabled));

      toast({
        title: installed ? 'Cron SAFE installé' : 'Cron partiellement installé',
        description: installed
          ? 'Les jobs NAS sont installés. Active le flag quand ton NAS est prêt.'
          : 'Vérifie pg_cron/pg_net puis réessaie.',
      });
    } catch (error: any) {
      toast({
        title: 'Installation cron impossible',
        description: error?.message || 'Exécute CREATE_CONFIGURE_NAS_BACKUP_CRON_RPC.sql puis réessaie.',
        variant: 'destructive',
      });
    } finally {
      setNasCronLoading(false);
    }
  };

  const toggleNasBackupFlag = async (enabled: boolean) => {
    try {
      setNasBackupToggleLoading(true);

      const { error } = await supabase
        .from('site_settings')
        .upsert({
          key: 'nas_backup_enabled',
          value: enabled,
          category: 'backups',
          data_type: 'boolean',
          description: 'Active ou suspend les cron backups NAS (BDD + fichiers)',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'key' });

      if (error) throw error;

      setNasBackupEnabled(enabled);
      toast({
        title: enabled ? 'Backups NAS automatiques activés' : 'Backups NAS automatiques désactivés',
        description: enabled
          ? 'Les jobs cron NAS pourront s\'exécuter selon la planification.'
          : 'Les jobs cron NAS restent installés mais ne s\'exécuteront plus.',
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error?.message || 'Impossible de changer le statut des backups NAS automatiques',
        variant: 'destructive',
      });
    } finally {
      setNasBackupToggleLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
    fetchConfig();
  }, [fetchBackups, fetchConfig]);

  // Sauvegarder la configuration
  const saveConfig = async () => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert({
          key: 'database_backup_config',
          value: JSON.stringify(config),
          updated_at: new Date().toISOString()
        }, { onConflict: 'key' });

      if (error) throw error;

      toast({
        title: "Configuration sauvegardée",
        description: "Les paramètres de sauvegarde ont été mis à jour.",
      });

      setConfigDialog(false);
    } catch (error: any) {
      console.error('Error saving config:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de sauvegarder la configuration",
        variant: "destructive"
      });
    }
  };

  // Lancer une sauvegarde manuelle
  const startBackup = async () => {
    try {
      setBackupInProgress(true);
      setBackupProgress(0);

      // Simuler la progression
      const progressInterval = setInterval(() => {
        setBackupProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 500);

      // Appeler l'Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/database-backup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            backup_type: 'manual',
            destination: config.destination,
            tables: config.include_tables,
            compress: config.compress,
            encrypt: config.encrypt
          })
        }
      );

      clearInterval(progressInterval);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Backup failed');
      }

      setBackupProgress(100);

      toast({
        title: "Sauvegarde terminée",
        description: `${result.tables_count || 0} tables sauvegardées (${result.file_size_mb?.toFixed(2) || '?'} MB)`,
      });

      // Rafraîchir la liste
      setTimeout(() => {
        fetchBackups();
        setBackupInProgress(false);
        setBackupProgress(0);
      }, 1000);

    } catch (error: any) {
      console.error('Backup error:', error);
      setBackupInProgress(false);
      setBackupProgress(0);
      
      toast({
        title: "Échec de la sauvegarde",
        description: error.message || "Une erreur est survenue",
        variant: "destructive"
      });
    }
  };

  // Sauvegarde locale (téléchargement direct)
  const startLocalBackup = async () => {
    try {
      setBackupInProgress(true);
      setBackupProgress(0);

      // Simuler la progression
      const progressInterval = setInterval(() => {
        setBackupProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 20;
        });
      }, 300);

      // Appeler l'Edge Function avec destination locale
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/database-backup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            backup_type: 'manual',
            destination: 'local', // Téléchargement direct
            tables: config.include_tables,
            compress: config.compress,
            encrypt: config.encrypt
          })
        }
      );

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Backup failed');
      }

      // Récupérer le blob et déclencher le téléchargement
      const blob = await response.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `backup_${timestamp}.json`;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setBackupProgress(100);

      toast({
        title: "Sauvegarde téléchargée",
        description: `Fichier ${fileName} téléchargé avec succès`,
      });

      // Rafraîchir la liste et réinitialiser
      setTimeout(() => {
        fetchBackups();
        setBackupInProgress(false);
        setBackupProgress(0);
      }, 1000);

    } catch (error: any) {
      console.error('Local backup error:', error);
      setBackupInProgress(false);
      setBackupProgress(0);
      
      toast({
        title: "Échec du téléchargement",
        description: error.message || "Une erreur est survenue",
        variant: "destructive"
      });
    }
  };

  // Sauvegarde des fichiers Storage (images, documents)
  const startStorageBackup = async () => {
    try {
      setStorageBackupInProgress(true);

      toast({
        title: "Sauvegarde Storage en cours",
        description: "Exportation des fichiers (images, documents)...",
      });

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/storage-backup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            backup_type: 'manual',
            destination: 'local',
            buckets: STORAGE_BUCKETS
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Storage backup failed');
      }

      // Récupérer les infos du header
      const totalFiles = response.headers.get('X-Total-Files') || '?';
      const totalSizeMB = response.headers.get('X-Total-Size-MB') || '?';

      // Récupérer le blob ZIP et déclencher le téléchargement
      const blob = await response.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `storage_backup_${timestamp}.zip`;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Sauvegarde Storage terminée",
        description: `${totalFiles} fichiers exportés (${totalSizeMB} MB)`,
      });

    } catch (error: any) {
      console.error('Storage backup error:', error);
      toast({
        title: "Échec de la sauvegarde Storage",
        description: error.message || "Une erreur est survenue",
        variant: "destructive"
      });
    } finally {
      setStorageBackupInProgress(false);
    }
  };

  const checkNasConnection = async (): Promise<boolean> => {
    try {
      setNasHealthChecking(true);
      setNasHealthStatus('unknown');

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nas-backup-health`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
        }
      );

      const result = await response.json().catch(() => ({}));
      const ok = response.ok && result?.success;

      if (ok) {
        setNasHealthStatus('ok');
        setNasHealthMessage(result?.message || 'Connexion NAS valide');
        toast({ title: 'Test NAS réussi', description: result?.message || 'Connexion valide' });
        return true;
      }

      const message = getReadableError(result?.error || result?.message || 'Connexion NAS impossible');
      setNasHealthStatus('error');
      setNasHealthMessage(message);
      toast({ title: 'Test NAS échoué', description: message, variant: 'destructive' });
      return false;
    } catch (error: any) {
      const message = getReadableError(error?.message || 'Connexion NAS impossible');
      setNasHealthStatus('error');
      setNasHealthMessage(message);
      toast({ title: 'Test NAS échoué', description: message, variant: 'destructive' });
      return false;
    } finally {
      setNasHealthChecking(false);
    }
  };

  // Sauvegarde complète vers NAS Synology (BDD + fichiers)
  const startNasBackup = async () => {
    try {
      setNasBackupInProgress(true);

      const nasReady = await checkNasConnection();
      if (!nasReady) {
        throw new Error('Connexion NAS non valide. Vérifiez la configuration.');
      }

      const { data: { session } } = await supabase.auth.getSession();

      const dbResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/database-backup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            backup_type: 'manual',
            destination: 'nas',
            tables: config.include_tables,
            compress: config.compress,
            encrypt: config.encrypt
          })
        }
      );

      const dbResult = await dbResponse.json();
      if (!dbResponse.ok) {
        throw new Error(dbResult.error || 'Backup BDD vers NAS échoué');
      }

      const storageResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/storage-backup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            backup_type: 'manual',
            destination: 'nas',
            buckets: STORAGE_BUCKETS
          })
        }
      );

      const storageResult = await storageResponse.json();
      if (!storageResponse.ok) {
        throw new Error(storageResult.error || 'Backup fichiers vers NAS échoué');
      }

      toast({
        title: 'Sauvegarde NAS terminée',
        description: 'Base de données et fichiers envoyés vers le NAS Synology.',
      });

      fetchBackups();
    } catch (error: any) {
      console.error('NAS backup error:', error);
      toast({
        title: 'Échec sauvegarde NAS',
        description: getReadableError(error.message || 'Une erreur est survenue'),
        variant: 'destructive'
      });
    } finally {
      setNasBackupInProgress(false);
    }
  };

  // Télécharger une sauvegarde
  const downloadBackup = async (backup: BackupRecord) => {
    if (!backup.file_name) {
      toast({
        title: "Fichier non disponible",
        description: "Le fichier de sauvegarde n'est pas accessible",
        variant: "destructive"
      });
      return;
    }

    try {
      // Créer une URL signée pour le téléchargement (valide 1 heure)
      const { data, error } = await supabase.storage
        .from('backups')
        .createSignedUrl(backup.file_name, 3600);

      if (error) throw error;

      // Ouvrir l'URL de téléchargement
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = backup.file_name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast({
        title: "Téléchargement démarré",
        description: `${backup.file_name}`,
      });
    } catch (error: any) {
      console.error('Download error:', error);
      toast({
        title: "Échec du téléchargement",
        description: error.message || "Erreur d'accès au fichier",
        variant: "destructive"
      });
    }
  };

  // Gérer la sélection du fichier de restauration
  const handleRestoreFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: "Format invalide",
        description: "Veuillez sélectionner un fichier JSON de sauvegarde",
        variant: "destructive"
      });
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      const normalizedTables = data.tables || data.data;

      // Valider la structure du fichier
      if (!data.metadata || !normalizedTables) {
        throw new Error("Format de fichier de sauvegarde invalide");
      }

      const tables = Object.keys(normalizedTables);
      const totalRows = Object.values(normalizedTables).reduce(
        (acc: number, rows: any) => acc + (Array.isArray(rows) ? rows.length : 0), 
        0
      );

      setRestoreFile(file);
      setRestorePreview({ tables, totalRows });
    } catch (error: any) {
      console.error('Error parsing backup file:', error);
      toast({
        title: "Fichier invalide",
        description: error.message || "Impossible de lire le fichier de sauvegarde",
        variant: "destructive"
      });
    }
  };

  // Restaurer à partir d'un fichier
  const startRestore = async () => {
    if (!restoreFile) return;

    try {
      setRestoreInProgress(true);
      setRestoreProgress(0);

      // Lire le contenu du fichier
      const text = await restoreFile.text();
      const parsed = JSON.parse(text);
      const backupData = {
        ...parsed,
        tables: parsed.tables || parsed.data,
      };

      // Simuler la progression
      const progressInterval = setInterval(() => {
        setRestoreProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 300);

      // Appeler l'Edge Function de restauration
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/database-restore`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            backup_data: backupData,
            mode: restoreMode
          })
        }
      );

      clearInterval(progressInterval);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Restore failed');
      }

      setRestoreProgress(100);

      if (result?.dry_run) {
        toast({
          title: "Simulation terminée",
          description: `${result.tables_count || 0} tables analysées (${result.rows_count || 0} lignes)`
        });
      } else {
        toast({
          title: "Restauration terminée",
          description: `${result.tables_restored || 0} tables restaurées (${result.rows_restored || 0} lignes)`,
        });
      }

      // Fermer le dialog et réinitialiser
      setTimeout(() => {
        setRestoreDialog(false);
        setRestoreFile(null);
        setRestorePreview(null);
        setRestoreInProgress(false);
        setRestoreProgress(0);
      }, 1500);

    } catch (error: any) {
      console.error('Restore error:', error);
      setRestoreInProgress(false);
      setRestoreProgress(0);
      
      toast({
        title: "Échec de la restauration",
        description: error.message || "Une erreur est survenue",
        variant: "destructive"
      });
    }
  };

  // Supprimer une sauvegarde
  const deleteBackup = async () => {
    if (!selectedBackup) return;

    try {
      // Supprimer le fichier du storage
      if (selectedBackup.file_name) {
        await supabase.storage.from('backups').remove([selectedBackup.file_name]);
      }

      // Supprimer l'enregistrement
      const { error } = await (supabase as any)
        .from('database_backups')
        .delete()
        .eq('id', selectedBackup.id);

      if (error) throw error;

      toast({
        title: "Sauvegarde supprimée",
        description: "La sauvegarde a été supprimée avec succès",
      });

      setConfirmDialog(false);
      setSelectedBackup(null);
      fetchBackups();
    } catch (error: any) {
      console.error('Delete error:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Formater la taille
  const formatSize = (mb: number | null) => {
    if (!mb) return '-';
    if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
    if (mb > 1000) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  // Formater la date
  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Badge de statut
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Terminée</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" /> Échouée</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> En cours</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800"><Clock className="h-3 w-3 mr-1" /> En attente</Badge>;
    }
  };

  // Badge de destination
  const getDestinationBadge = (dest: string) => {
    switch (dest) {
      case 'local':
        return <Badge variant="outline"><HardDrive className="h-3 w-3 mr-1" /> Local</Badge>;
      case 'cloud':
        return <Badge variant="outline"><Cloud className="h-3 w-3 mr-1" /> Cloud</Badge>;
      case 'nas':
        return <Badge variant="outline"><HardDrive className="h-3 w-3 mr-1" /> NAS Synology</Badge>;
      case 'both':
        return <Badge variant="outline"><Database className="h-3 w-3 mr-1" /> Cloud + NAS</Badge>;
      default:
        return <Badge variant="outline">{dest}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            Sauvegardes Base de Données
          </h3>
          <p className="text-muted-foreground mt-1">
            Gérez les sauvegardes automatiques et manuelles de votre base de données
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button 
            variant="outline" 
            onClick={() => setRestoreDialog(true)}
            disabled={restoreInProgress}
          >
            <Upload className="h-4 w-4 mr-2" />
            Restaurer
          </Button>
          <Button variant="outline" onClick={() => setConfigDialog(true)}>
            <Settings2 className="h-4 w-4 mr-2" />
            Configuration
          </Button>
          <Button 
            variant="secondary"
            onClick={startLocalBackup} 
            disabled={backupInProgress}
          >
            {backupInProgress ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                En cours...
              </>
            ) : (
              <>
                <HardDrive className="h-4 w-4 mr-2" />
                Sauvegarder BDD
              </>
            )}
          </Button>
          <Button 
            variant="secondary"
            onClick={startStorageBackup} 
            disabled={storageBackupInProgress || nasBackupInProgress}
          >
            {storageBackupInProgress ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Export fichiers...
              </>
            ) : (
              <>
                <FileArchive className="h-4 w-4 mr-2" />
                Exporter fichiers (ZIP)
              </>
            )}
          </Button>
          <Button 
            variant="secondary"
            onClick={startNasBackup}
            disabled={nasBackupInProgress || backupInProgress || storageBackupInProgress}
          >
            {nasBackupInProgress ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Envoi NAS...
              </>
            ) : (
              <>
                <Database className="h-4 w-4 mr-2" />
                Sauvegarder vers NAS
              </>
            )}
          </Button>
          <Button 
            onClick={startBackup} 
            disabled={backupInProgress || nasBackupInProgress}
            className="bg-primary"
          >
            {backupInProgress ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sauvegarde en cours...
              </>
            ) : (
              <>
                <Cloud className="h-4 w-4 mr-2" />
                Sauvegarder sur le cloud
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Progression de la sauvegarde */}
      {backupInProgress && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Sauvegarde en cours...</span>
                <span className="text-sm text-muted-foreground">{Math.round(backupProgress)}%</span>
              </div>
              <Progress value={backupProgress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                Export des tables, compression et envoi vers le stockage...
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statut de la planification */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Sauvegarde automatique
            </CardTitle>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => {
                  setConfig(prev => ({ ...prev, enabled: checked }));
                  // Sauvegarder immédiatement le changement
                  supabase.from('site_settings').upsert({
                    key: 'database_backup_config',
                    value: JSON.stringify({ ...config, enabled: checked }),
                    updated_at: new Date().toISOString()
                  }, { onConflict: 'key' });
                }}
              />
              <span className={config.enabled ? 'text-green-600 font-medium' : 'text-gray-500'}>
                {config.enabled ? 'Activée' : 'Désactivée'}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Planification:</span>
              <span className="font-medium">
                {SCHEDULE_PRESETS.find(p => p.value === config.schedule)?.label || config.schedule}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Cloud className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Destination:</span>
              {getDestinationBadge(config.destination)}
            </div>
            <div className="flex items-center gap-2">
              <FileArchive className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Compression:</span>
              <span className="font-medium">{config.compress ? 'Oui' : 'Non'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Chiffrement:</span>
              <span className="font-medium">{config.encrypt ? 'Oui' : 'Non'}</span>
            </div>
          </div>

          <div className="mt-4 rounded-md border p-4 bg-muted/20">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Backups NAS automatiques</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Contrôle le flag nas_backup_enabled utilisé par les cron SAFE.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={nasCronInstalled ? 'default' : 'secondary'}>
                  {nasCronInstalled ? 'Cron installé' : 'Cron non installé'}
                </Badge>
                <Badge variant={nasBackupEnabled ? 'default' : 'secondary'}>
                  {nasBackupEnabled ? 'Activé' : 'Désactivé'}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={installSafeNasCron}
                disabled={nasCronLoading}
              >
                {nasCronLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Installation...
                  </>
                ) : (
                  'Installer/MAJ cron SAFE'
                )}
              </Button>
              <Button
                size="sm"
                onClick={() => toggleNasBackupFlag(true)}
                disabled={nasBackupToggleLoading || nasBackupEnabled || !nasCronInstalled}
              >
                {nasBackupToggleLoading && !nasBackupEnabled ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Activation...
                  </>
                ) : (
                  'Activer'
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => toggleNasBackupFlag(false)}
                disabled={nasBackupToggleLoading || !nasBackupEnabled || !nasCronInstalled}
              >
                {nasBackupToggleLoading && nasBackupEnabled ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Désactivation...
                  </>
                ) : (
                  'Désactiver'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historique des sauvegardes */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Historique des sauvegardes</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchBackups}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Aucune sauvegarde enregistrée</p>
              <p className="text-sm">Lancez une sauvegarde manuelle ou activez la planification</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Détails</TableHead>
                  <TableHead>Tables</TableHead>
                  <TableHead>Taille</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((backup) => {
                  const duration = backup.completed_at && backup.started_at
                    ? Math.round((new Date(backup.completed_at).getTime() - new Date(backup.started_at).getTime()) / 1000)
                    : null;

                  return (
                    <TableRow key={backup.id}>
                      <TableCell className="font-medium">
                        {formatDate(backup.started_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={backup.backup_type === 'manual' ? 'default' : 'secondary'}>
                          {backup.backup_type === 'manual' ? 'Manuel' : 'Planifié'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getDestinationBadge(backup.destination)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(backup.status)}
                      </TableCell>
                      <TableCell className="max-w-[320px] text-xs text-muted-foreground">
                        {backup.error_message
                          ? backup.error_message.slice(0, 160)
                          : (backup.file_name || '-')}
                      </TableCell>
                      <TableCell>
                        {backup.tables_count || '-'}
                      </TableCell>
                      <TableCell>
                        {formatSize(backup.file_size_mb)}
                      </TableCell>
                      <TableCell>
                        {duration !== null ? `${duration}s` : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {backup.status === 'completed' && backup.file_name && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => downloadBackup(backup)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedBackup(backup);
                              setConfirmDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de configuration */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configuration des sauvegardes</DialogTitle>
            <DialogDescription>
              Configurez les paramètres de sauvegarde automatique de la base de données
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Activation */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Sauvegardes automatiques</Label>
                <p className="text-sm text-muted-foreground">
                  Activer les sauvegardes planifiées
                </p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            <Separator />

            {/* Planification */}
            <div className="space-y-3">
              <Label>Fréquence</Label>
              <Select
                value={schedulePreset || SCHEDULE_PRESETS.find(p => p.value === config.schedule)?.value || 'custom'}
                onValueChange={(value) => {
                  setSchedulePreset(value);
                  if (value !== 'custom') {
                    setConfig(prev => ({ ...prev, schedule: value }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une fréquence" />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      <div className="flex flex-col">
                        <span>{preset.label}</span>
                        <span className="text-xs text-muted-foreground">{preset.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Personnalisé</SelectItem>
                </SelectContent>
              </Select>
              {schedulePreset === 'custom' && (
                <Input
                  placeholder="Expression cron (ex: 0 2 * * *)"
                  value={config.schedule}
                  onChange={(e) => setConfig(prev => ({ ...prev, schedule: e.target.value }))}
                />
              )}
            </div>

            {/* Destination */}
            <div className="space-y-3">
              <Label>Destination</Label>
              <Select
                value={config.destination}
                onValueChange={(value: 'local' | 'cloud' | 'nas' | 'both') => 
                  setConfig(prev => ({ ...prev, destination: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cloud">
                    <div className="flex items-center gap-2">
                      <Cloud className="h-4 w-4" />
                      <span>Supabase Storage (Cloud)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="local">
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-4 w-4" />
                      <span>Stockage local</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="both">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      <span>Cloud + NAS Synology</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="nas">
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-4 w-4" />
                      <span>NAS Synology (distant)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                <div className="text-sm">
                  <p className="font-medium">Test connexion NAS</p>
                  <p className="text-muted-foreground">{nasHealthMessage}</p>
                </div>
                <div className="flex items-center gap-2">
                  {nasHealthStatus === 'ok' && <Wifi className="h-4 w-4 text-green-600" />}
                  {nasHealthStatus === 'error' && <WifiOff className="h-4 w-4 text-red-600" />}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={checkNasConnection}
                    disabled={nasHealthChecking}
                  >
                    {nasHealthChecking ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Test...
                      </>
                    ) : (
                      'Tester NAS'
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Rétention */}
            <div className="space-y-3">
              <Label>Rétention (jours)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={config.retention_days}
                onChange={(e) => setConfig(prev => ({ 
                  ...prev, 
                  retention_days: parseInt(e.target.value) || 30 
                }))}
              />
              <p className="text-xs text-muted-foreground">
                Les sauvegardes plus anciennes seront automatiquement supprimées
              </p>
            </div>

            <Separator />

            {/* Options avancées */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Compression</Label>
                  <p className="text-xs text-muted-foreground">Réduire la taille des fichiers</p>
                </div>
                <Switch
                  checked={config.compress}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, compress: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Chiffrement</Label>
                  <p className="text-xs text-muted-foreground">Sécuriser les données</p>
                </div>
                <Switch
                  checked={config.encrypt}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, encrypt: checked }))}
                />
              </div>
            </div>

            {/* Alerte info */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Les sauvegardes automatiques utilisent pg_cron de Supabase. 
                Assurez-vous que l'extension est activée dans votre projet.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveConfig}>
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation suppression */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la sauvegarde ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. La sauvegarde sera définitivement supprimée.
            </DialogDescription>
          </DialogHeader>
          {selectedBackup && (
            <div className="py-4">
              <p className="text-sm">
                <strong>Date:</strong> {formatDate(selectedBackup.started_at)}
              </p>
              <p className="text-sm">
                <strong>Taille:</strong> {formatSize(selectedBackup.file_size_mb)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={deleteBackup}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de restauration */}
      <Dialog open={restoreDialog} onOpenChange={(open) => {
        if (!restoreInProgress) {
          setRestoreDialog(open);
          if (!open) {
            setRestoreFile(null);
            setRestorePreview(null);
          }
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Restaurer une sauvegarde
            </DialogTitle>
            <DialogDescription>
              Importez un fichier JSON de sauvegarde pour restaurer les données
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Zone de sélection de fichier */}
            {!restoreFile ? (
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-3">
                  Glissez-déposez un fichier ou cliquez pour sélectionner
                </p>
                <Input
                  type="file"
                  accept=".json"
                  onChange={handleRestoreFileChange}
                  className="max-w-xs mx-auto cursor-pointer"
                />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Aperçu du fichier */}
                <div className="border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileArchive className="h-5 w-5 text-primary" />
                      <span className="font-medium">{restoreFile.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRestoreFile(null);
                        setRestorePreview(null);
                      }}
                      disabled={restoreInProgress}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>

                  {restorePreview && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Tables:</span>
                        <span className="ml-2 font-medium">{restorePreview.tables.length}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Lignes:</span>
                        <span className="ml-2 font-medium">{restorePreview.totalRows.toLocaleString()}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Tables incluses:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {restorePreview.tables.slice(0, 8).map(table => (
                            <Badge key={table} variant="outline" className="text-xs">
                              {table}
                            </Badge>
                          ))}
                          {restorePreview.tables.length > 8 && (
                            <Badge variant="secondary" className="text-xs">
                              +{restorePreview.tables.length - 8} autres
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Progression de la restauration */}
                {restoreInProgress && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Restauration en cours...</span>
                      <span>{Math.round(restoreProgress)}%</span>
                    </div>
                    <Progress value={restoreProgress} className="h-2" />
                  </div>
                )}

                {/* Avertissement */}
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Attention:</strong> Cette opération va fusionner les données de la sauvegarde 
                    avec les données existantes. Les enregistrements avec le même ID seront mis à jour.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Mode de restauration</Label>
                  <Select
                    value={restoreMode}
                    onValueChange={(value: 'dry_run' | 'merge' | 'replace') => setRestoreMode(value)}
                    disabled={restoreInProgress}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dry_run">Simulation (dry-run)</SelectItem>
                      <SelectItem value="merge">Fusion (merge)</SelectItem>
                      <SelectItem value="replace">Remplacement (replace)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRestoreDialog(false)}
              disabled={restoreInProgress}
            >
              Annuler
            </Button>
            <Button 
              onClick={startRestore}
              disabled={!restoreFile || restoreInProgress}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {restoreInProgress ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restauration...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {restoreMode === 'dry_run' ? 'Prévisualiser' : 'Restaurer'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
